import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { PaymentData, ProcessSecuredFieldsPaymentInput } from "@jti/fe-common";
import { getUser, PgsApi } from "@jti/rest-api";

import {
  checkAdapterSupportsSecuredFields,
  getPaymentAdapterScript,
  loadAdapterScript,
} from "../domain/paymentAdapter/paymentAdapter";
import { initiatePaymentProcess, mountPaymentAdapter } from "../domain/paymentGateway/paymentGateway";

type PaymentAdapterStatus = "idle" | "loading" | "ready" | "error";
type PaymentInitiationStatus = "idle" | "loading" | "error"; // the "ready" state is represented by redirect via payment adapter
type SecuredFieldsPaymentInitiationStatus = "idle" | "loading" | "error";

type InitiatePaymentParams = {
  orderId: string;
  orderEntityId: string;
  paymentMethodCode: string;
  isSubscription: boolean;
};

type MountAdapterParams = {
  orderId: string;
  orderEntityId: string;
  paymentMethodCode: string;
  isSubscription: boolean;
  mountElement: HTMLElement;
};

type MountAdapterResult = {
  paymentToken: string;
  unmount: () => void;
};

type ProcessSecuredFieldsPaymentParams = Omit<ProcessSecuredFieldsPaymentInput, "consumerId">;

export type PaymentContextValue = {
  /**
   * adapterStatus refers to the last submitted payment method via onSectionSubmit from Payment Method Section
   */
  adapterStatus: PaymentAdapterStatus;
  paymentInitiationStatus: PaymentInitiationStatus;
  /**
   * True from the moment mountAdapter resolves until its returned unmount() is called.
   * Reflects whether a card payment widget is actually mounted, so consumers don't
   * need to track a separate, manually-synced "in progress" flag.
   */
  isPaymentSessionActive: boolean;
  secureFieldsPaymentInitiationStatus: SecuredFieldsPaymentInitiationStatus;
  preloadAdapter: (paymentMethodCode: string) => Promise<void>;
  initiatePayment: (initiatePaymentParams: InitiatePaymentParams) => Promise<void>;
  mountAdapter: (params: MountAdapterParams) => Promise<MountAdapterResult>;
  checkSecuredFieldsSupport: (paymentMethodCode: string) => boolean;
  createPaymentSession: (paymentMethod: string) => Promise<{ session_id: string; merchant: string }>;
  processSecuredFieldsPayment: (params: ProcessSecuredFieldsPaymentParams) => Promise<PaymentData>;
};

const PaymentContext = createContext<PaymentContextValue | null>(null);

export const PaymentProvider = ({ children }: { children: ReactNode }) => {
  const config = useConfig();
  const [adapterStatus, setAdapterStatus] = useState<PaymentAdapterStatus>("idle");
  const [paymentInitiationStatus, setPaymentInitiationStatus] = useState<PaymentInitiationStatus>("idle");
  const [isPaymentSessionActive, setIsPaymentSessionActive] = useState(false);
  const [secureFieldsPaymentInitiationStatus, setSecureFieldsPaymentInitiationStatus] =
    useState<SecuredFieldsPaymentInitiationStatus>("idle");
  const preloadedPaymentMethodsRef = useRef(new Set<string>());
  const preloadingPaymentMethodsRef = useRef(new Map<string, Promise<void>>());

  const preloadAdapter = useCallback((paymentMethodCode: string): Promise<void> => {
    if (preloadedPaymentMethodsRef.current.has(paymentMethodCode)) {
      setAdapterStatus("ready");
      return Promise.resolve();
    }

    const existingPreload = preloadingPaymentMethodsRef.current.get(paymentMethodCode);

    if (existingPreload) {
      return existingPreload;
    }

    setAdapterStatus("loading");

    const preload = (async () => {
      try {
        const scriptUrl = await getPaymentAdapterScript(paymentMethodCode);
        await loadAdapterScript(scriptUrl);
        preloadedPaymentMethodsRef.current.add(paymentMethodCode);
        setAdapterStatus("ready");
      } catch (error) {
        setAdapterStatus("error");
        throw error;
      } finally {
        preloadingPaymentMethodsRef.current.delete(paymentMethodCode);
      }
    })();

    preloadingPaymentMethodsRef.current.set(paymentMethodCode, preload);
    return preload;
  }, []);

  const initiatePayment = useCallback(
    async ({ orderId, orderEntityId, paymentMethodCode, isSubscription }: InitiatePaymentParams): Promise<void> => {
      try {
        setPaymentInitiationStatus("loading");

        await initiatePaymentProcess({
          orderId,
          orderEntityId,
          paymentMethodCode,
          config,
          isFreeTrial: false,
          isSubscription,
          merchant: config.paymentMerchantName,
        });
      } catch (error) {
        setPaymentInitiationStatus("error");
        throw error;
      }
    },
    [config]
  );

  const checkSecuredFieldsSupport = useCallback((paymentMethodCode: string): boolean => {
    return checkAdapterSupportsSecuredFields(paymentMethodCode);
  }, []);

  const mountAdapter = useCallback(
    async ({
      orderId,
      orderEntityId,
      paymentMethodCode,
      isSubscription,
      mountElement,
    }: MountAdapterParams): Promise<MountAdapterResult> => {
      try {
        setPaymentInitiationStatus("loading");

        const result = await mountPaymentAdapter({
          orderId,
          orderEntityId,
          paymentMethodCode,
          config,
          isFreeTrial: false,
          isSubscription,
          merchant: config.paymentMerchantName,
          mountElement,
        });

        setPaymentInitiationStatus("idle");
        setIsPaymentSessionActive(true);

        return {
          ...result,
          unmount: () => {
            result.unmount();
            setIsPaymentSessionActive(false);
          },
        };
      } catch (error) {
        setPaymentInitiationStatus("error");
        throw error;
      }
    },
    [config]
  );

  const createPaymentSession = useCallback(
    async (paymentMethod: string): Promise<{ session_id: string; merchant: string }> => {
      const pgsApi = PgsApi.init();

      if (!pgsApi.createPaymentSession) {
        throw new Error("createPaymentSession is not supported by the current PGS API");
      }

      return pgsApi.createPaymentSession(paymentMethod);
    },
    []
  );

  const processSecuredFieldsPayment = useCallback(
    async (params: ProcessSecuredFieldsPaymentParams): Promise<PaymentData> => {
      setSecureFieldsPaymentInitiationStatus("loading");

      try {
        const user = getUser();

        if (!user?.uuid) {
          throw new Error("User is not authenticated");
        }

        const pgsApi = PgsApi.init();

        if (!pgsApi.processSecuredFieldsPayment) {
          throw new Error("processSecuredFieldsPayment is not supported by the current PGS API");
        }

        const payment = await pgsApi.processSecuredFieldsPayment({
          ...params,
          consumerId: user.uuid,
        });

        setSecureFieldsPaymentInitiationStatus("idle");
        return payment;
      } catch (error) {
        setSecureFieldsPaymentInitiationStatus("error");
        throw error;
      }
    },
    []
  );

  const value: PaymentContextValue = useMemo(
    () => ({
      adapterStatus,
      paymentInitiationStatus,
      isPaymentSessionActive,
      secureFieldsPaymentInitiationStatus,
      preloadAdapter,
      initiatePayment,
      mountAdapter,
      checkSecuredFieldsSupport,
      createPaymentSession,
      processSecuredFieldsPayment,
    }),
    [
      adapterStatus,
      paymentInitiationStatus,
      isPaymentSessionActive,
      secureFieldsPaymentInitiationStatus,
      preloadAdapter,
      initiatePayment,
      mountAdapter,
      checkSecuredFieldsSupport,
      createPaymentSession,
      processSecuredFieldsPayment,
    ]
  );

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};

export const usePaymentProviderContext = (): PaymentContextValue => {
  const ctx = useContext(PaymentContext);
  if (!ctx) {
    throw new Error("usePaymentProviderContext must be used within PaymentProviderContext");
  }
  return ctx;
};
